import { useState } from 'react';
import FileUpload from 'components/FileUpload';
import DownloadBox from 'components/DownloadBox';
import Icon from 'components/Icon';
import Seo from 'components/Seo';
import { mkUrl } from 'utils/api';
import ToolSeoContent from 'components/ToolSeoContent';

/**
 * Generic tool page wrapper.
 * Props (added for SEO): seoTitle, seoDesc, path
 *   iconName, title, desc, accept, hint, buttonLabel, apiCall,
 *   outputFilename, multiple, children, infoText
 */
export default function ToolPage({
  iconName, title, desc, accept, hint, buttonLabel, apiCall,
  outputFilename, multiple, children, infoText,
  seoTitle, seoDesc, path, seoKey,
}) {
  const [file, setFile]     = useState(multiple ? [] : null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [dlUrl, setDlUrl]   = useState('');
  const [stats, setStats]   = useState(null);
  const [extra, setExtra]   = useState({});

  const reset = () => {
    setFile(multiple ? [] : null);
    setError(''); setDlUrl(''); setStats(null); setExtra({});
  };

  const handleSubmit = async () => {
    const hasFile = multiple ? file.length > 0 : !!file;
    if (!hasFile) { setError('Please select a file first.'); return; }
    setLoading(true); setError(''); setDlUrl(''); setStats(null);
    try {
      const fd = new FormData();
      if (multiple) file.forEach(f => fd.append('files', f));
      else fd.append('file', file);
      Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
      const res = await apiCall(fd);
      setDlUrl(mkUrl(res.data));
      const origSize  = res.headers['x-original-size'];
      const compSize  = res.headers['x-compressed-size'];
      const savedPct  = res.headers['x-saved-percent'];
      if (origSize && compSize) {
        setStats([
          { value: fmtSize(parseInt(origSize)),  label: 'Original Size' },
          { value: fmtSize(parseInt(compSize)),  label: 'New Size' },
          { value: `${savedPct || 0}%`,          label: 'Size Reduced' },
        ]);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <div className="tool-pg">
      {/* SEO: uses seoTitle/seoDesc if given, otherwise falls back to title/desc */}
      <Seo title={seoTitle || title} description={seoDesc || desc} path={path} />

      <div className="container" style={{ maxWidth: 720 }}>
        <div className="tool-hdr">
          <div className="tool-hdr-row">
            <div className="tool-hdr-icon"><Icon name={iconName} size={24} /></div>
            <div><h1>{title}</h1><p>{desc}</p></div>
          </div>
        </div>

        {!dlUrl ? (
          <div className="tool-box">
            <FileUpload
              accept={accept} multiple={multiple} onFileChange={setFile}
              hint={hint} label="Click or drag & drop your file here"
            />
            {children && children({ extra, setExtra })}
            {error && (
              <div className="alert alert-error" style={{ marginTop: 14 }}>
                <Icon name="alertCircle" size={16} />{error}
              </div>
            )}
            <button
              className="btn btn-primary btn-lg btn-full"
              style={{ marginTop: 20 }}
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading
                ? <><span className="spinner sp-sm" />Processing...</>
                : <><Icon name={iconName} size={18} />{buttonLabel || 'Convert'}</>
              }
            </button>
          </div>
        ) : (
          <DownloadBox url={dlUrl} filename={outputFilename} onReset={reset} stats={stats} />
        )}

        {infoText && (
          <div className="alert alert-info" style={{ marginTop: 14 }}>
            <Icon name="info" size={16} /><span>{infoText}</span>
          </div>
        )}
        {seoKey && <ToolSeoContent tool={seoKey} />}
      </div>
    </div>
  );
}

function fmtSize(bytes) {
  if (!bytes) return '0 B';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
}
